import { message } from 'antd';
import axios from 'axios';
import { cancelled, all, select, fork, cancel, race, call, put, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { push, replace } from 'redux-little-router';
import { defineCategory, fetchCategories, receiveCategories } from '../../reducers/categoryReducer';
import dbTabsSagas from '../db/dbTabsSagas';
import categoriesAPI from '../../api/categoriesAPI';
import entityActions from '../../genericActions/entityActions';
import sagaHelpers from '../../helpers/sagaHelpers';
import categoryActions from '../../../Tabs/Category/categoryActions';

const createWorkflow = function* createWorkflow(tabId, action) {
  const connection = yield select(store => store.connection.byId[store.connection['/window'].ids[0]]);
  const { keyWindow, id, data } = action.payload;

  // Verify a category doesn't already exist by this name
  try {
    const response = yield call(categoriesAPI.getCategory, connection, id);

    // This category already exists
    if (response) {
      message.error('Category already exists! Please choose a different name.');
      yield put(entityActions.receiveErrors(
        keyWindow,
      ));
      return undefined;
    }
  } catch (err) {
    // Only treat this as an error if the response isn't 404.
    if (!(err.response && err.response.status === 404)) {
      message.error('Error reaching server, please try again.');
      yield put(entityActions.receiveErrors(
        keyWindow,
      ));
      return undefined;
    }

    // Otherwise, continue since its safe to make the category.
  }


  try {
    const response = yield call(categoriesAPI.putCategory, connection, id, data);
    yield call(dbTabsSagas.updateCategorySaga, connection.id, tabId, id);
    yield put(entityActions.receiveEntities(
      keyWindow,
      {
        type: 'categories',
        byId: { [response.id]: response },
        ids: [response.id],
      },
    ));
    return response;
  } catch (err) {
    message.error('Error creating category.');
    yield put(entityActions.receiveErrors(
      keyWindow,
    ));
  } finally {
    if (yield cancelled()) {
      // Perform cleanup
    }
  }
};

/*
const updateWorkflow = function* updateWorkflow(keyWindow, id) {
  const connection = yield select(store => store.connection.byId[store.connection['/window'].ids[0]]);
  try {
    while (true) {
      const { undo, save } = yield race({
        undo: take(action => (action.type === 'UNDO_UPDATE_CATEGORY' && action.payload.id === id)),
        update: take(action => (action.type === 'UPDATE_CATEGORY' && action.payload.id === id)),
        save: take(sagaHelpers.generateKeyWindowActionMatcher('SAVE_CATEGORY', keyWindow)),
      });

      if (undo) {
        return yield put(categoryActions.undoUpdateCategory(keyWindow, id));
      } else if (save) {
        try {
          const data = yield select(store => store.category.draftsById[id]);
          const response = yield call(categoriesAPI.putCategory, connection, id, data);

          yield put(entityActions.receiveEntities(keyWindow, { categories: [response] }));
          return response;
        } catch (err) {
          console.error(err);
          // TODO: handle_error
        }
      }
    }
  } catch (err) {
    console.error(err);
  } finally {
    if (yield cancelled()) {
      // Perform cleanup
    }
  }
};
*/

const saveWorkflow = function* saveWorkflow(action) {
  const connection = yield select(store => store.connection.byId[store.connection['/window'].ids[0]]);
  const { keyWindow, id, data } = action.payload;

  try {
    const response = yield call(categoriesAPI.putCategory, connection, id, data);
    yield put(entityActions.receiveEntities(
      keyWindow,
      {
        type: 'categories',
        byId: { [response.id]: response },
        ids: [response.id],
      },
    ));
    return response;
  } catch (err) {
    message.error('Error saving category.');

    yield put(entityActions.receiveErrors(
      keyWindow,
    ));
  } finally {
    if (yield cancelled()) {
      // Perform cleanup
    }
  }
};

const fetchWorkflow = function* fetchWorkflow(keyWindow, id) {
  const connection = yield select(store => store.connection.byId[store.connection['/window'].ids[0]]);
  try {
    let response;
    try {
      response = yield call(categoriesAPI.getCategory, connection, id);
    } catch (err) {
      console.error(err);
      // TODO: handle_error
    }

    if (response) {
      yield put(entityActions.receiveEntities(
        keyWindow,
        {
          type: 'categories',
          byId: { [response.id]: response },
          ids: [response.id],
        },
      ));
      return response;
    }
    return undefined;
  } catch (err) {
    message.error('Error loading category.');
    yield put(entityActions.receiveErrors(
      keyWindow,
    ));
  } finally {
    if (yield cancelled()) {
      // Perform cleanup
    }
  }
};

const removeWorkflow = function* removeWorkflow(action) {
  const connection = yield select(store => store.connection.byId[store.connection['/window'].ids[0]]);
  try {
    let error;
    try {
      yield call(categoriesAPI.deleteCategory, connection, action.payload.id);
    } catch (err) {
      message.error('Error deleting category.');
      yield put(entityActions.receiveErrors(
        action.payload.keyWindow,
      ));
      // TODO: handle_error
      error = err;
    }

    if (!error) {
      return true;
    }
    return false;
  } finally {
    if (yield cancelled()) {
      // Perform cleanup
    }
  }
};


const mainWorkflow = function* mainWorkflow(tabId) {
  try {
    const tab = yield select(store => store.tab.byId[tabId]);
    const tabCategory = tab.category;
    if (!tabCategory.sourceId) {
      yield put(categoryActions.newCategory(`/tab/${tabId}`, tabCategory.id));

      let newCategory;
      while (!newCategory) {
        const create = yield take(sagaHelpers.generateKeyWindowActionMatcher('CREATE_CATEGORY', `/tab/${tabId}`));
        newCategory = yield call(createWorkflow, tabId, create);
      }
    } else {
      try {
        yield call(fetchWorkflow, `/tab/${tabId}`, tabCategory.sourceId);
      } catch (err) {
        console.error(err);
        cancel();
      }
    }

    while (true) {
      const { save, fetch, remove } = yield race({
        save: take(sagaHelpers.generateKeyWindowActionMatcher('SAVE_CATEGORY', `/tab/${tabId}`)),
        fetch: take(sagaHelpers.generateKeyWindowActionMatcher('FETCH_CATEGORY', `/tab/${tabId}`)),
        remove: take(sagaHelpers.generateKeyWindowActionMatcher('REMOVE_CATEGORY', `/tab/${tabId}`)),
      });

      if (remove) {
        if (yield call(removeWorkflow, remove)) yield cancel();
      } else if (save) {
        yield call(saveWorkflow, save);
      } else if (fetch) {
        yield call(fetchWorkflow);
      }
    }
  } catch (e) {
    console.log(e);
    // handle failure
  } finally {
    if (yield cancelled()) {
      yield put(entityActions.releaseKeyWindow(`/tab/${tabId}`));
    }
  }
};

export default mainWorkflow;
